/**
 * The freshness badge.
 *
 * Every live value in uSolar is shown next to one of these. It states the
 * verdict in words (not only colour, per WCAG 2.2) together with the age of
 * the measurement, so a four-hour-old reading can never look live (§1.4, §12).
 */

import { useTranslation } from 'react-i18next';

import type { Freshness, SourceStatus } from '../api/types';
import { describeAge, formatDateTime } from '../lib/format';

const ICONS: Record<SourceStatus, string> = {
  online: '●',
  stale: '◐',
  offline: '○',
  unknown: '?',
  error: '!',
};

interface Props {
  freshness: Freshness | null | undefined;
  compact?: boolean;
}

export function FreshnessBadge({ freshness, compact = false }: Props) {
  const { t } = useTranslation();
  const status: SourceStatus = freshness?.status ?? 'unknown';
  const age = describeAge(freshness?.age_seconds ?? null);

  const title =
    freshness?.sample_time
      ? t('freshness.tooltip', {
          sampleTime: formatDateTime(freshness.sample_time),
          cycle: freshness.expected_cycle_seconds,
        })
      : t('freshness.never');

  return (
    <span className={`badge badge--${status}`} title={title} data-status={status}>
      <span aria-hidden="true">{ICONS[status]}</span>
      <span>{t(`freshness.${status}`)}</span>
      {!compact && age ? <span className="badge__age">· {t(age.key, { value: age.value })}</span> : null}
    </span>
  );
}
